<script setup>
import { gameTypeLabel } from '../services/game-label'
import { businessCalendarDate } from '../../../shared/business-day.js'
/**
 * 牌九「账单统计」弹窗，逐条对齐旧版 web/19.0d8589924b85d555a0a0.js 里的
 * app-bill 组件（类 _、模板工厂 Hn + 子模板 xn/wn/In、样式 kn）。
 *
 * 旧版打开点：右侧菜单「账单」→ billModal()，
 *   modalCtrl.create({ component: _, componentProps: { game } })
 * 注意这跟独立路由页 /game/bill（chunk 23，标题「游戏账单」，带日期选择器）不是同一个东西，
 * 弹窗版没有日期输入框，日期区间固定为「今天往前推 6 天 ~ 今天」。
 *
 * 旧版构造函数：
 *   this.startTime = navParams.get('start') || moment().add(-6,'days').format('YYYY-MM-DD')
 *   this.endTime   = navParams.get('end')   || moment().format('YYYY-MM-DD')
 * ngOnInit → getBillLists()
 *   getBill({game: game.game_id, start, end, show_day: true}) → POST /game/bet/bill
 *   成功后 lists = data，即 { total: {...}, lists: [...] }
 * doRefresh(l) → getBillLists(l)，l 是 ion-refresher 事件，完成后 l.target.complete()
 */
import { computed, onMounted, ref } from 'vue'
import { request } from '../services/api'
import PullRefresher from './PullRefresher.vue'
import PaijiuBetListModal from './PaijiuBetListModal.vue'

const props = defineProps({
  // 旧版 navParams.get('game')：完整 game 对象，请求只用到 game_id
  game: { type: Object, required: true },
  // 旧版 navParams.get('start') / get('end')，弹窗版调用点没传，走默认值
  start: { type: String, default: '' },
  end: { type: String, default: '' },
})
const emit = defineEmits(['close'])

// 旧版 lists 初始是 undefined，模板两段 ion-list 都是 *ngIf="lists"，
// 所以加载完成前整个 content 是空白的，这里用 null 复刻。
const lists = ref(null)
const errorMessage = ref('')

/** 旧版 moment().add(-6,'days').format('YYYY-MM-DD') */
function ymd(offsetDays = 0) {
  const date = businessCalendarDate()
  date.setDate(date.getDate() + offsetDays)
  const pad = (number) => String(number).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const startTime = ref(props.start || ymd(-6))
const endTime = ref(props.end || ymd(0))

const gameId = computed(() => props.game?.game_id)

// 旧版模板用 Angular DecimalPipe 的 '.2-2'：固定两位小数 + 千位分隔符（默认 locale en-US）
const decimal2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
function fmt2(value) { return decimal2.format(Number(value || 0)) }

/**
 * 旧版日期列是 `{{ start_time | amDateFormat:'yyyy-MM-DD' }}`，
 * amDateFormat 就是 moment(v).format(fmt)（main.js 模块 LWQm）。
 * 打包进去的 moment 是 2.24.0，它的 formattingTokens 正则里没有 `y` 令牌
 * （`y{2,4}` 是 2.25+ 才加的），所以 `yyyy` 会被当字面量原样输出 ——
 * 旧版实际渲染成 `yyyy-08-01`，年份是死的 "yyyy" 四个字母，这是旧版的 bug。
 *
 * 这里按旧版意图修正：输出真实年份，即 2026-08-01。
 * 后端 bill() 返回的 start_time/end_time 是 'Y-m-d H:i:s'，取前 10 位即可。
 */
function formatDate(value) {
  if (!value) return ''
  const matched = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!matched) return String(value)
  return `${matched[1]}-${matched[2]}-${matched[3]}`
}

/** 旧版 [ngClass]="{win: bet_money_pl>0, lost: bet_money_pl<0}" */
function signClass(value) { return { win: Number(value) > 0, lost: Number(value) < 0 } }

/** 旧版 getBillLists(l=null)，l 是 refresher 事件 */
async function getBillLists(event = null) {
  if (!gameId.value) { event?.complete(); return }
  errorMessage.value = ''
  try {
    const response = await request('/game/bet/bill', {
      method: 'POST',
      data: {
        game: gameId.value,
        start: startTime.value,
        end: endTime.value,
        // 后端 show_day + start + end 同时存在时才返回 {total, lists}，明细最多 30 天
        show_day: true,
      },
    })
    lists.value = response.data || null
  } catch (error) {
    errorMessage.value = error.message || '加载失败'
  } finally {
    // 旧版 subscribe 回调结尾的 l && l.target.complete()
    event?.complete()
  }
}

/** 旧版 doRefresh(l)：直接重拉，账单接口没有分页 */
function doRefresh(event) { void getBillLists(event) }

/**
 * 旧版 showBetList(start, end)：再叠一层 modal
 *   modalCtrl.create({ component: f（app-bet 注单列表）, componentProps: {game, start, end} })
 */
const betListModal = ref(null)
function showBetList(start, end) {
  betListModal.value = { start, end }
}

// 旧版 ngOnInit
onMounted(() => { void getBillLists() })
</script>

<template>
  <div class="bill-modal">
    <!-- 旧版 ion-header > ion-toolbar：左侧 arrow-back + 标题「账单统计」，右侧没有按钮 -->
    <header class="modal-header">
      <button class="modal-back" type="button" aria-label="返回" @click="emit('close')">
        <!-- 旧版 ion-icon[name=arrow-back]，md 模式渲染的是 Material arrow_back -->
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </button>
      <h1 class="modal-title">账单统计<span v-if="gameTypeLabel(game)"> · {{ gameTypeLabel(game) }}</span></h1>
      <!-- 右侧等宽占位，抵掉左侧返回按钮宽度，让标题严格居中 -->
      <span class="modal-spacer" aria-hidden="true"></span>
    </header>

    <!-- 旧版 ion-content 内挂 ion-refresher，参数照抄：
         pullingIcon="arrow-dropdown" pullingText="下拉刷新"
         refreshingSpinner="circles" refreshingText="下拉刷新..." -->
    <PullRefresher
      pulling-text="下拉刷新"
      refreshing-text="下拉刷新..."
      refreshing-spinner="circles"
      @refresh="doRefresh"
    >
      <!-- 旧版 xn：*ngIf="lists" 的「总计」表 -->
      <section v-if="lists" class="ion-list">
        <div class="list-header"> 总计 </div>
        <div class="list-item">
          <div class="list-item-inner">
            <div class="bill-lists-item">
              <div class="item-date"> 日期 </div>
              <div class="item-bet"> 下注 </div>
              <div class="item-win"> 有效 </div>
              <div class="item-back"> 反水 </div>
              <div class="item-pl"> 盈利 </div>
            </div>
          </div>
        </div>
        <div class="list-item">
          <div class="list-item-inner">
            <div class="bill-lists-item">
              <!-- 旧版总计行的日期列是三个 div：开始、" - "、结束 -->
              <div class="item-date">
                <div>{{ formatDate(lists.total?.start_time) }}</div>
                <div> - </div>
                <div>{{ formatDate(lists.total?.end_time) }}</div>
              </div>
              <div class="item-bet"> {{ fmt2(lists.total?.bet_money) }} </div>
              <div class="item-win"> {{ fmt2(lists.total?.bet_money_valid) }} </div>
              <div class="item-back"> {{ fmt2(lists.total?.bet_money_back) }} </div>
              <div
                class="item-pl"
                tappable
                :class="signClass(lists.total?.bet_money_pl)"
                @click="showBetList(lists.total?.start_time, lists.total?.end_time)"
              >
                {{ fmt2(lists.total?.bet_money_pl) }}
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 旧版 wn：*ngIf="lists" 的「明细」表，行模板 In -->
      <section v-if="lists" class="ion-list">
        <div class="list-header"> 明细 </div>
        <div class="list-item">
          <div class="list-item-inner">
            <div class="bill-lists-item">
              <div class="item-date"> 日期 </div>
              <div class="item-bet"> 下注 </div>
              <div class="item-win"> 有效 </div>
              <div class="item-back"> 反水 </div>
              <div class="item-pl"> 盈利 </div>
            </div>
          </div>
        </div>
        <div v-for="(row, index) in (lists.lists || [])" :key="index" class="list-item">
          <div class="list-item-inner">
            <div class="bill-lists-item">
              <!-- 旧版明细行日期列只有一个值：start_time -->
              <div class="item-date"> {{ formatDate(row.start_time) }} </div>
              <div class="item-bet"> {{ fmt2(row.bet_money) }} </div>
              <div class="item-win"> {{ fmt2(row.bet_money_valid) }} </div>
              <div class="item-back"> {{ fmt2(row.bet_money_back) }} </div>
              <div
                class="item-pl"
                tappable
                :class="signClass(row.bet_money_pl)"
                @click="showBetList(row.start_time, row.end_time)"
              >
                {{ fmt2(row.bet_money_pl) }}
              </div>
            </div>
          </div>
        </div>
      </section>

      <p v-if="errorMessage" class="modal-notice">{{ errorMessage }}</p>
    </PullRefresher>

    <!-- 旧版 showBetList() 叠上来的注单列表 modal（component f = app-bet） -->
    <PaijiuBetListModal
      v-if="betListModal"
      :game="game"
      :start="betListModal.start"
      :end="betListModal.end"
      @close="betListModal = null"
    />
  </div>
</template>

<style scoped>
/* 弹窗外壳与顶栏跟 PaijiuStatisticModal 保持一致（旧版两个 modal 的 ion-header 结构完全相同） */
.bill-modal{position:fixed;z-index:30;top:0;left:0;width:100%;height:100%;background:#fff;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif}
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;font-size:22px;line-height:1;color:#fff;cursor:pointer;padding:0}
.modal-back .modal-icon{width:24px;height:24px;display:block}
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:#fff}
.modal-spacer{width:56px;flex:none}

/* --- 以下三块是 Ionic 4 md 模式的真实默认值，从 web/135.773ae5d979ae5c36fbef.js 挖出来 --- */

/* ion-list（.list-md）：padding-top/bottom 各 8px，背景 #fff */
.ion-list{display:block;margin:0;padding:8px 0;background:#fff;list-style-type:none}

/* ion-list-header（md）：min-height 45px、padding-left 16px、font-size 14px、color #000 */
.list-header{display:flex;align-items:center;justify-content:space-between;width:100%;margin:0;padding:0 0 0 16px;min-height:45px;font-size:14px;background:transparent;color:#000;overflow:hidden}

/* ion-item（md）拆成两层复刻：
   .list-item      = ion-item 的 .item-native：min-height 48px、padding-left 16px、flex 居中
   .list-item-inner= ion-item 的 .item-inner  ：flex:1、padding-right 16px、下边框 1px rgba(0,0,0,.13)
   字号来自全局 styles.css 的 `ion-item{font-size:.8rem}` */
.list-item{display:flex;align-items:center;justify-content:space-between;width:100%;min-height:48px;margin:0;padding:0 0 0 16px;background:#fff;color:#000;font-size:.8rem;overflow:hidden;box-sizing:border-box}
.list-item-inner{display:flex;flex:1;flex-direction:inherit;align-items:inherit;align-self:stretch;min-height:inherit;padding:0 16px 0 0;border-width:0 0 1px 0;border-style:solid;border-color:rgba(0,0,0,.13);overflow:hidden;box-sizing:border-box}

/* --- 以下逐条照抄旧版样式表 kn（chunk 19 偏移 115547），:host → .bill-modal --- */
.lists-header{justify-content:flex-start}
.info-title{color:#666;display:inline-block;margin-right:10px}
.info-value{display:inline-block}
.win{color:red}
.lost{color:green}
.bill-lists-item{width:100%;display:flex;align-items:center;justify-content:space-between;font-size:.8rem}
.bill-lists-item .item-date{width:20%;text-align:left}
.bill-lists-item .item-bet{width:20%;text-align:right}
.bill-lists-item .item-win{width:20%;text-align:right}
.bill-lists-item .item-back{width:20%;text-align:right}
.bill-lists-item .item-pl{width:20%;text-align:right}
/* Ionic 全局给 [tappable] 加的 cursor:pointer，kn 里没写但实际生效 */
.bill-lists-item .item-pl[tappable]{cursor:pointer}

.modal-notice{margin:10px 16px;color:#999;font-size:.8rem;text-align:center}
</style>
